/**
 * BCF Import Modal
 * Import tasks from BCF (BIM Collaboration Format) files
 */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, FileArchive, Check, AlertTriangle, Image, MessageSquare, Tag, Calendar, User } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getApiUrl } from '../utils/api';

const BCFImportModal = ({ isOpen, onClose, currentUser, projectId, projectName, onImportComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('upload'); // upload, preview, importing, done
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${getApiUrl()}/import/bcf/preview?user_id=${currentUser.id}`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPreviewData(data);
        // Select all topics by default
        setSelectedTopics(new Set(data.topics.map(t => t.guid)));
        setStep('preview');
      } else {
        setError(data.detail || t('import.bcf.readError'));
      }
    } catch (err) {
      console.error('BCF Preview error:', err);
      setError(t('import.bcf.uploadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData || selectedTopics.size === 0) return;

    setLoading(true);
    setStep('importing');
    setError(null);

    try {
      // Filter selected topics
      const topicsToImport = previewData.topics.filter(t => selectedTopics.has(t.guid));

      const response = await fetch(`${getApiUrl()}/import/bcf/execute?user_id=${currentUser.id}&project_id=${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topics: topicsToImport,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setImportResult(data);
        setStep('done');
        if (onImportComplete) {
          onImportComplete(data);
        }
      } else {
        setError(data.detail || t('import.bcf.importError'));
        setStep('preview');
      }
    } catch (err) {
      console.error('BCF Import error:', err);
      setError(t('import.bcf.importError'));
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (guid) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(guid)) {
        next.delete(guid);
      } else {
        next.add(guid);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTopics.size === previewData.topics.length) {
      setSelectedTopics(new Set());
    } else {
      setSelectedTopics(new Set(previewData.topics.map(t => t.guid)));
    }
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError(null);
    setSelectedTopics(new Set());
    onClose();
  };

  const getPriorityColor = (priority) => {
    return priority === 'urgent' ? 'destructive' : 'secondary';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-orange-500 to-amber-500 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileArchive className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-bold">{t('import.bcf.title')}</h2>
                <p className="text-sm text-orange-100">
                  {projectName ? `→ ${projectName}` : t('import.bcf.subtitle')}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Upload Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50/50 transition-all"
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">
                  {t('import.bcf.dropFile')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t('import.bcf.fileTypes')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".bcf,.bcfzip"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 text-orange-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                  <span>{t('import.bcf.analyzing')}</span>
                </div>
              )}

              {/* BCF Info */}
              <div className="bg-orange-50 rounded-lg p-4">
                <h3 className="font-medium text-orange-800 mb-2">{t('import.bcf.whatIs')}</h3>
                <p className="text-sm text-orange-700 mb-3">
                  {t('import.bcf.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-orange-700 border-orange-300">
                    <Image className="w-3 h-3 mr-1" /> {t('import.bcf.snapshots')}
                  </Badge>
                  <Badge variant="outline" className="text-orange-700 border-orange-300">
                    <MessageSquare className="w-3 h-3 mr-1" /> {t('import.bcf.comments')}
                  </Badge>
                  <Badge variant="outline" className="text-orange-700 border-orange-300">
                    <Tag className="w-3 h-3 mr-1" /> {t('import.bcf.metadata')}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && previewData && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileArchive className="w-8 h-8 text-orange-600" />
                  <div>
                    <p className="font-medium">{previewData.filename}</p>
                    <p className="text-sm text-gray-500">
                      BCF {previewData.bcf_version} • {previewData.topic_count} Topics
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={toggleAll}>
                  {selectedTopics.size === previewData.topics.length ? t('import.bcf.deselectAll') : t('import.bcf.selectAll')}
                </Button>
              </div>

              {/* Topics List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {previewData.topics.map((topic) => (
                  <div
                    key={topic.guid}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedTopics.has(topic.guid)
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleTopic(topic.guid)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                        selectedTopics.has(topic.guid)
                          ? 'bg-orange-500 border-orange-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedTopics.has(topic.guid) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>

                      {/* Snapshot thumbnail */}
                      {topic.snapshot && (
                        <img
                          src={topic.snapshot}
                          alt="BCF Snapshot"
                          className="w-16 h-16 object-cover rounded border"
                        />
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-gray-800 truncate">
                            {topic.title || 'Untitled Topic'}
                          </h4>
                          <Badge variant={getPriorityColor(topic.priority)}>
                            {topic.priority === 'urgent' ? t('tasks.high') : t('tasks.medium')}
                          </Badge>
                          <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(topic.status)}`}>
                            {topic.status}
                          </span>
                        </div>
                        
                        {topic.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {topic.description}
                          </p>
                        )}

                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          {topic.type && (
                            <span className="flex items-center gap-1">
                              <Tag className="w-3 h-3" /> {topic.type}
                            </span>
                          )}
                          {topic.assigned_to && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" /> {topic.assigned_to}
                            </span>
                          )}
                          {topic.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {topic.due_date}
                            </span>
                          )}
                          {topic.comments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" /> {topic.comments.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {previewData.topics.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>{t('import.bcf.noTopics')}</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mx-auto mb-6"></div>
              <p className="text-lg font-medium text-gray-700">{t('import.bcf.importing')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('import.bcf.creatingTasks')}</p>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && importResult && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('import.bcf.success')}</h3>
              
              <div className="bg-green-50 rounded-lg p-6 my-6 inline-block">
                <p className="text-3xl font-bold text-green-700">{importResult.tasks_created}</p>
                <p className="text-sm text-green-600">{t('import.bcf.tasksCreated')}</p>
              </div>

              {importResult.errors?.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-left max-w-md mx-auto">
                  <h4 className="font-medium text-yellow-800 mb-1">{t('common.warnings')}:</h4>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between">
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          )}
          
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => { setStep('upload'); setFile(null); setPreviewData(null); }}>
                {t('import.bcf.chooseOther')}
              </Button>
              <Button
                onClick={handleImport}
                disabled={loading || selectedTopics.size === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {loading ? t('import.bcf.importing') : `${selectedTopics.size} Topics ${t('import.bcf.import')}`}
              </Button>
            </>
          )}

          {step === 'done' && (
            <Button onClick={handleClose} className="ml-auto bg-green-600 hover:bg-green-700">
              {t('common.close')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BCFImportModal;
